import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DndContext, DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { ArrowLeft, Play, Square, Save, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { WorkflowCanvas } from '../components/workflow-builder/WorkflowCanvas'
import { StepPalette } from '../components/workflow-builder/StepPalette'
import { StepConfigForm } from '../components/workflow-builder/StepConfigForm'
import { LiveBrowserViewer } from '../components/anchor-browser/LiveBrowserViewer'
import { validateWorkflow } from '../lib/workflow/validation'
import { api } from '../lib/api'
import { anchorBrowserApi } from '../lib/anchor-browser-api'
import type { WorkflowStep, WorkflowExecution, NavigateUrlParams, TaskParams, VerifyParams } from '../types/workflow'
import type { BrowserSession } from '../lib/api/sessions'

interface WorkflowBuilderState {
  steps: WorkflowStep[]
  selectedStepId: string | null
  execution: WorkflowExecution | null
  browserSession: BrowserSession | null
  sessionHealth: any
  isRunning: boolean
  isSaving: boolean
}

export function WorkflowBuilder() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { toast } = useToast()
  const isEditing = !!id

  const [state, setState] = useState<WorkflowBuilderState>({
    steps: [
      {
        id: 'start-block',
        workflow_id: 0,
        step_index: 0,
        step_type: 'navigate_url',
        params: {
          url: 'https://google.com'
        },
        enabled: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ],
    selectedStepId: null,
    execution: null,
    browserSession: null,
    sessionHealth: null,
    isRunning: false,
    isSaving: false
  })

  const [, setDraggedStep] = useState<WorkflowStep | null>(null)

  const loadWorkflow = useCallback(async (workflowId: string) => {
    try {
      // For now, just load the basic workflow info
      // TODO: Implement proper workflow loading with steps
      console.log('Loading workflow:', workflowId)
    } catch (error) {
      console.error('Failed to load workflow:', error)
      toast({
        title: 'Error',
        description: 'Failed to load workflow',
        variant: 'destructive'
      })
    }
  }, [toast])

  // Load workflow if editing
  useEffect(() => {
    if (isEditing && id) {
      loadWorkflow(id)
    }
  }, [id, isEditing, loadWorkflow])

  // Step management functions
  const handleStepSelect = useCallback((stepId: string | null) => {
    setState(prev => ({ ...prev, selectedStepId: stepId }))
  }, [])

  const handleStepUpdate = useCallback((stepId: string, updates: Partial<WorkflowStep>) => {
    setState(prev => ({
      ...prev,
      steps: prev.steps.map(step =>
        step.id === stepId ? { ...step, ...updates } : step
      )
    }))
  }, [])

  const handleStepDelete = useCallback((stepId: string) => {
    setState(prev => ({
      ...prev,
      steps: prev.steps.filter(step => step.id !== stepId),
      selectedStepId: prev.selectedStepId === stepId ? null : prev.selectedStepId
    }))
  }, [])

  const handleStepReorder = useCallback((oldIndex: number, newIndex: number) => {
    setState(prev => ({
      ...prev,
      steps: arrayMove(prev.steps, oldIndex, newIndex)
    }))
  }, [])

  // Browser session management
  const handleStartSession = useCallback(async () => {
    try {
      const session = await anchorBrowserApi.sessions.createSession({})
      setState(prev => ({ ...prev, browserSession: session }))
      toast({
        title: 'Session Started',
        description: 'Browser session is now active'
      })
    } catch (error) {
      console.error('Failed to start session:', error)
      toast({
        title: 'Error',
        description: 'Failed to start browser session',
        variant: 'destructive'
      })
    }
  }, [toast])

  const handleStopSession = useCallback(async () => {
    if (state.browserSession) {
      try {
        await anchorBrowserApi.sessions.stopSession(state.browserSession.id)
        setState(prev => ({ ...prev, browserSession: null }))
        toast({
          title: 'Session Stopped',
          description: 'Browser session has been closed'
        })
      } catch (error) {
        console.error('Failed to stop session:', error)
      }
    }
  }, [state.browserSession, toast])

  // Workflow execution
  const executeStep = useCallback(async (step: WorkflowStep, sessionId: string): Promise<any> => {
    console.log(`Executing step: ${step.step_type}`, step)

    try {
      let taskRequest: any = {
        session_id: sessionId,
        sync: true // Use synchronous execution
      }

      switch (step.step_type) {
        case 'navigate_url': {
          const navParams = step.params as NavigateUrlParams
          taskRequest = {
            ...taskRequest,
            task: `Navigate to the URL: ${navParams.url || 'https://google.com'}`,
            url: navParams.url || 'https://google.com',
            max_agent_steps: 3
          }
          break
        }

        case 'task': {
          const taskParams = step.params as TaskParams
          taskRequest = {
            ...taskRequest,
            task: taskParams.description || 'Search for "Playwright automation" on the current page',
            max_agent_steps: 10
          }
          break
        }

        case 'take_screenshot':
          taskRequest = {
            ...taskRequest,
            task: 'Take a screenshot of the current page',
            max_agent_steps: 1
          }
          break

        case 'verify': {
          const verifyParams = step.params as VerifyParams
          taskRequest = {
            ...taskRequest,
            task: verifyParams.description || 'Verify the page content is as expected',
            max_agent_steps: 3
          }
          break
        }

        default:
          throw new Error(`Unsupported step type: ${step.step_type}`)
      }

      console.log('Creating task with request:', taskRequest)
      const result = await anchorBrowserApi.createTask(taskRequest)
      console.log('Task result:', result)

      return result
    } catch (error) {
      console.error(`Error executing step ${step.step_type}:`, error)
      throw error
    }
  }, [])

  const handleRunWorkflow = useCallback(async () => {
    const validation = validateWorkflow(state.steps)
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join(', '),
        variant: 'destructive'
      })
      return
    }

    setState(prev => ({ ...prev, isRunning: true }))

    try {
      // Ensure we have a browser session
      let sessionId = state.browserSession?.id
      if (!sessionId) {
        const session = await anchorBrowserApi.sessions.createSession({})
        sessionId = session.id
        setState(prev => ({ ...prev, browserSession: session }))
      }

      // Execute steps sequentially
      const enabledSteps = state.steps.filter(step => step.enabled)
      const execution: WorkflowExecution = {
        workflow_id: 0,
        status: 'running',
        current_step_index: 0,
        started_at: new Date().toISOString(),
        step_results: []
      }

      setState(prev => ({ ...prev, execution }))

      for (let i = 0; i < enabledSteps.length; i++) {
        const step = enabledSteps[i]

        // Update current step
        setState(prev => ({
          ...prev,
          execution: prev.execution ? {
            ...prev.execution,
            current_step_index: i
          } : null
        }))

        try {
          console.log(`Executing step ${i + 1}/${enabledSteps.length}: ${step.step_type}`)

          await executeStep(step, sessionId)

          // Update step result
          setState(prev => ({
            ...prev,
            execution: prev.execution ? {
              ...prev.execution,
              step_results: [
                ...prev.execution.step_results,
                {
                  step_id: step.id,
                  status: 'success',
                  finished_at: new Date().toISOString()
                }
              ]
            } : null
          }))

          console.log(`Step ${i + 1} completed successfully`)

        } catch (error) {
          console.error(`Step ${i + 1} failed:`, error)

          // Update step result with error
          setState(prev => ({
            ...prev,
            execution: prev.execution ? {
              ...prev.execution,
              status: 'failed',
              step_results: [
                ...prev.execution.step_results,
                {
                  step_id: step.id,
                  status: 'failed',
                  finished_at: new Date().toISOString(),
                  error_message: error instanceof Error ? error.message : 'Unknown error'
                }
              ]
            } : null
          }))

          toast({
            title: 'Workflow Failed',
            description: `Step ${i + 1} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            variant: 'destructive'
          })

          setState(prev => ({ ...prev, isRunning: false }))
          return
        }
      }

      // Workflow completed successfully
      setState(prev => ({
        ...prev,
        execution: prev.execution ? {
          ...prev.execution,
          status: 'success',
          finished_at: new Date().toISOString()
        } : null,
        isRunning: false
      }))

      toast({
        title: 'Workflow Completed',
        description: `Successfully executed ${enabledSteps.length} steps`
      })

    } catch (error) {
      console.error('Workflow execution failed:', error)
      setState(prev => ({ ...prev, isRunning: false }))
      toast({
        title: 'Workflow Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    }
  }, [state.steps, state.browserSession, toast, executeStep])

  const handleStopWorkflow = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRunning: false,
      execution: prev.execution ? {
        ...prev.execution,
        status: 'stopped',
        finished_at: new Date().toISOString()
      } : null
    }))

    toast({
      title: 'Workflow Stopped',
      description: 'Workflow execution has been stopped'
    })
  }, [toast])

  const handleSaveWorkflow = useCallback(async () => {
    setState(prev => ({ ...prev, isSaving: true }))

    try {
      const workflowData = {
        name: 'My Workflow',
        description: 'Workflow created in builder',
        base_url: 'https://google.com',
        steps: state.steps
      }

      if (isEditing && id) {
        await api.updateWorkflow(parseInt(id), workflowData)
        toast({
          title: 'Workflow Saved',
          description: 'Workflow has been updated successfully'
        })
      } else {
        const newWorkflow = await api.createWorkflow(workflowData)
        navigate(`/workflow-builder/${newWorkflow.id}`)
        toast({
          title: 'Workflow Created',
          description: 'New workflow has been created successfully'
        })
      }
    } catch (error) {
      console.error('Failed to save workflow:', error)
      toast({
        title: 'Error',
        description: 'Failed to save workflow',
        variant: 'destructive'
      })
    } finally {
      setState(prev => ({ ...prev, isSaving: false }))
    }
  }, [state.steps, isEditing, id, navigate, toast])

  // Drag and drop handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event
    if (active.data.current?.type === 'step-template') {
      setDraggedStep(active.data.current.step)
    }
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setDraggedStep(null)

    if (!over) return

    if (active.data.current?.type === 'step-template' && over.id === 'workflow-canvas') {
      // Add new step from palette
      const stepTemplate = active.data.current.step
      const newStep: WorkflowStep = {
        id: `step-${Date.now()}`,
        workflow_id: 0,
        step_index: state.steps.length,
        step_type: stepTemplate.step_type,
        params: stepTemplate.params,
        enabled: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      setState(prev => ({
        ...prev,
        steps: [...prev.steps, newStep]
      }))
    } else if (active.data.current?.type === 'workflow-step' && over.data.current?.type === 'workflow-step') {
      // Reorder existing steps
      const activeIndex = state.steps.findIndex(step => step.id === active.id)
      const overIndex = state.steps.findIndex(step => step.id === over.id)

      if (activeIndex !== overIndex) {
        const newSteps = arrayMove(state.steps, activeIndex, overIndex)
        setState(prev => ({ ...prev, steps: newSteps }))
      }
    }
  }, [state.steps])

  const handleDragOver = useCallback((_event: DragOverEvent) => {
    // Handle drag over logic if needed
  }, [])

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between min-h-[80px]">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/workflows')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold truncate">
              {isEditing ? 'Edit Workflow' : 'Workflow Builder'}
            </h1>
            <p className="text-muted-foreground text-sm truncate">
              Build automation workflows with drag-and-drop
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {state.browserSession && (
            <Button variant="outline" size="sm" onClick={handleStopSession}>
              <Square className="h-4 w-4 mr-2" />
              Stop Session
            </Button>
          )}

          {!state.browserSession && (
            <Button variant="outline" size="sm" onClick={handleStartSession}>
              <Play className="h-4 w-4 mr-2" />
              Start Session
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveWorkflow}
            disabled={state.isSaving}
          >
            {state.isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>

          {state.isRunning ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleStopWorkflow}
            >
              <Square className="h-4 w-4 mr-2" />
              Stop
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleRunWorkflow}
              disabled={state.steps.length === 0}
            >
              <Play className="h-4 w-4 mr-2" />
              Run
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          {/* Left Panel - Steps and Palette */}
          <div className="w-80 border-r flex flex-col">
            {/* Step Palette */}
            <div className="h-1/2 border-b">
              <StepPalette />
            </div>

            {/* Workflow Canvas (Compact) */}
            <div className="flex-1">
              <WorkflowCanvas
                steps={state.steps}
                selectedStepId={state.selectedStepId}
                onStepSelect={handleStepSelect}
                onStepUpdate={handleStepUpdate}
                onStepDelete={handleStepDelete}
                onStepReorder={handleStepReorder}
                onRunWorkflow={handleRunWorkflow}
                onStopWorkflow={handleStopWorkflow}
                onStartSession={handleStartSession}
                onSaveWorkflow={handleSaveWorkflow}
                compact={true}
                execution={undefined}
                browserSession={undefined}
                sessionHealth={undefined}
              />
            </div>
          </div>

          {/* Right Panel - Browser Session or Step Config */}
          <div className="flex-1 flex flex-col">
            {state.browserSession ? (
              <LiveBrowserViewer
                liveUrl={state.browserSession.live_view_url}
                taskId={state.browserSession.id}
                taskStatus="running"
                className="flex-1"
              />
            ) : (
              <div className="flex-1 p-6 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <Play className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No Browser Session</h3>
                  <p className="mb-4">Start a browser session to see live automation</p>
                  <Button onClick={handleStartSession}>
                    <Play className="h-4 w-4 mr-2" />
                    Start Browser Session
                  </Button>
                </div>
              </div>
            )}

            {/* Step Configuration Panel */}
            {state.selectedStepId && (
              <div className="h-80 border-t">
                <StepConfigForm
                  step={state.steps.find(s => s.id === state.selectedStepId) || null}
                  isOpen={!!state.selectedStepId}
                  onSave={(stepId: string, updates: Partial<WorkflowStep>) => handleStepUpdate(stepId, updates)}
                  onClose={() => handleStepSelect(null)}
                />
              </div>
            )}
          </div>
        </DndContext>
      </div>
    </div>
  )
}